import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classnames from 'classnames'
import { useQuery } from '@tanstack/react-query'
import { getSuggestions, getSuggestionsKey } from '../../api-client/search'
import useDebouncedCallback from '../../hooks/use-debounced-callback'
import useClickOutside from '../../hooks/use-clickoutside'
import { zIndex } from '../../constants'
import Magnifier from '../svg/magnifier'
import { HeroSearch } from '../inputs'

const MAX_SUGGESTIONS_LENGTH = 10

export default function TypeaheadVehicleSearch({ onSubmit, onFocus, defaultValue = '', ...restProps }) {
  const wrapperRef = useRef()
  const [value, setValue] = useState(defaultValue)
  const [searchWord, setSearchWord] = useState(defaultValue || null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [hoveredSuggestionIndex, setHoveredSuggestionIndex] = useState(-1)

  useEffect(() => {
    setValue(defaultValue || '')
    setSearchWord(defaultValue || null)
  }, [defaultValue])

  const { data: suggestions = [] } = useQuery({
    queryKey: [getSuggestionsKey, searchWord],
    queryFn: () => getSuggestions({ term: searchWord }),
    enabled: !!searchWord,
    keepPreviousData: true,
  })

  const handleSuggestionsClickOutside = useCallback(() => {
    setShowSuggestions(false)
    setHoveredSuggestionIndex(-1)
  }, [])
  useClickOutside(wrapperRef, handleSuggestionsClickOutside)

  useEffect(() => {
    setShowSuggestions(!!searchWord?.length)
  }, [searchWord])

  const sortedSuggestions = useMemo(
    () => suggestions.slice(0, MAX_SUGGESTIONS_LENGTH).sort((a, b) => b.score - a.score),
    [suggestions],
  )

  const handleSuggestionSubmit = (searchTerms) => {
    setValue(searchTerms)
    setShowSuggestions(false)
    setHoveredSuggestionIndex(-1)
  }

  const debouncedChange = useDebouncedCallback(
    (value) => {
      setSearchWord(value)
      setHoveredSuggestionIndex(-1)
    },
    350,
    [],
  )

  useEffect(() => {
    if (typeof onSubmit === 'function') {
      onSubmit(value)
    }
  }, [value])

  const handleFocus = (event) => {
    event.persist()
    setShowSuggestions(true)
    if (typeof onFocus === 'function') {
      onFocus(event)
    }
  }

  const handleKeyDown = (event) => {
    const { key } = event

    switch (key) {
      case 'Escape': {
        event.preventDefault()
        setHoveredSuggestionIndex(-1)
        setShowSuggestions(false)
        break
      }
      case 'ArrowDown': {
        event.preventDefault()
        setHoveredSuggestionIndex((prev) => (prev + 1 === suggestions.length ? 0 : prev + 1))
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        setHoveredSuggestionIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1))
        break
      }
      case 'Enter': {
        event.preventDefault()

        let valueToSubmit = null
        if (hoveredSuggestionIndex > -1 && sortedSuggestions?.length > 0) {
          valueToSubmit = sortedSuggestions[hoveredSuggestionIndex]?.suggestedName
        }
        if (!valueToSubmit) {
          valueToSubmit = value
        }

        handleSuggestionSubmit(valueToSubmit)
        break
      }
      case 'Tab': {
        setHoveredSuggestionIndex(-1)
        setShowSuggestions(false)
        break
      }
      default:
        return
    }
  }

  const handleSuggestionClick = (index) => () => {
    const suggestion = sortedSuggestions[index]
    handleSuggestionSubmit(suggestion.suggestedName)
  }

  return (
    <div className='typeaheadVehicleSearch' ref={wrapperRef}>
      <HeroSearch
        {...restProps}
        adornment={<Magnifier fill='var(--secondary-text-color)' />}
        onChange={(e) => {
          debouncedChange(e.target.value)
          setValue(e.target.value)
        }}
        value={value}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
      {showSuggestions && (
        <ul
          className='suggestions'
          style={{ height: `${Math.min(suggestions.length, MAX_SUGGESTIONS_LENGTH) * 3.8}rem` }}
        >
          {sortedSuggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              className={classnames({ active: index === hoveredSuggestionIndex })}
            >
              <button
                className='flatBtn'
                onClick={handleSuggestionClick(index)}
                onMouseOver={(event) => {
                  event.preventDefault()
                  setHoveredSuggestionIndex(index)
                }}
              >
                {suggestion.suggestedName}
              </button>
            </li>
          ))}
        </ul>
      )}

      <style jsx>{`
        .typeaheadVehicleSearch {
          position: relative;
          max-width: 77.2rem;
          margin: 0 auto;
          font-size: 1.6rem;

          .suggestions {
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            overflow: hidden;
            background: #fff;
            border-radius: 1rem;

            li {
              button {
                width: 100%;
                height: 3.8rem;
                padding: 0 1rem;
                outline: none;
                text-align: left;
                font-weight: 700;
              }

              &.active,
              &:hover {
                background: var(--primary-color);
                color: #fff;
              }
            }
          }
        }
      `}</style>

      <style jsx>{`
        .typeaheadVehicleSearch {
          .suggestions {
            z-index: ${zIndex.Absolute};
          }
        }
      `}</style>
    </div>
  )
}
